// ThemePickerPage — /settings/theme.
//
// Edits the per-tenant `company_theme` row. Live preview updates the
// `--color-*` CSS custom properties under `:root` immediately as the user
// changes a swatch — no save is required to see the effect. On cancel the
// page re-injects the saved theme so the preview reverts.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Save } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import {
  ALL_THEME_FIELDS,
  DEFAULTS,
  THEME_GROUPS,
  injectThemePreview,
} from './themeFields';
import { useSaveTheme, useThemePickerQuery } from './useThemePicker';
import styles from './ThemePickerPage.module.css';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function pickThemeFields(src: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of ALL_THEME_FIELDS) {
    const v = src[f.key];
    if (typeof v === 'string' && HEX_RE.test(v)) {
      out[f.key] = v;
    } else {
      out[f.key] = f.defaultValue;
    }
  }
  return out;
}

export function ThemePickerPage(): JSX.Element {
  const navigate = useNavigate();
  const query = useThemePickerQuery();
  const save = useSaveTheme();
  const [working, setWorking] = useState<Record<string, string>>(() => ({
    ...DEFAULTS,
  }));
  const [resetOpen, setResetOpen] = useState(false);
  const initialised = useRef(false);
  const savedRef = useRef<Record<string, string>>({ ...DEFAULTS });

  // Hydrate working state from server response (once).
  useEffect(() => {
    if (!query.data || initialised.current) return;
    const picked = pickThemeFields(query.data as Record<string, unknown>);
    savedRef.current = picked;
    setWorking(picked);
    initialised.current = true;
  }, [query.data]);

  // Live preview — every time `working` changes.
  useEffect(() => {
    injectThemePreview(working);
  }, [working]);

  const dirty = useMemo(() => {
    const saved = savedRef.current;
    for (const k of Object.keys(working)) {
      if (working[k] !== saved[k]) return true;
    }
    return false;
  }, [working]);

  function setField(key: string, value: string) {
    setWorking((w) => ({ ...w, [key]: value }));
  }

  function resetField(key: string) {
    setWorking((w) => ({ ...w, [key]: DEFAULTS[key] ?? '#000000' }));
  }

  function handleCancel() {
    setWorking(savedRef.current);
    injectThemePreview(savedRef.current);
  }

  function handleSave() {
    // Validate all fields are valid hex.
    for (const [k, v] of Object.entries(working)) {
      if (!HEX_RE.test(v)) {
        alert(`Invalid hex value for ${k}: "${v}". Must be #RRGGBB.`);
        return;
      }
    }
    save.mutate(working, {
      onSuccess: (data) => {
        const picked = pickThemeFields(data as unknown as Record<string, unknown>);
        savedRef.current = picked;
        setWorking(picked);
      },
    });
  }

  function handleResetAll() {
    setWorking({ ...DEFAULTS });
    setResetOpen(false);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Theme"
        subtitle="Customise colours used across the app. Preview updates live; click Save Changes to persist."
        actions={
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/settings')}>
            Back
          </Button>
        }
      />

      <div className={styles.actionsBar}>
        <Button
          variant="secondary"
          icon={RotateCcw}
          onClick={() => setResetOpen(true)}
          disabled={save.isPending}
        >
          Reset all to defaults
        </Button>
        <div className={styles.spacer} />
        {dirty ? (
          <Button variant="ghost" onClick={handleCancel} disabled={save.isPending}>
            Cancel
          </Button>
        ) : null}
        <Button
          variant="primary"
          icon={Save}
          onClick={handleSave}
          loading={save.isPending}
          disabled={!dirty || save.isPending}
        >
          Save Changes
        </Button>
      </div>

      {THEME_GROUPS.map((group) => (
        <Card key={group.title} title={group.title}>
          {group.description ? (
            <p className={styles.groupDesc}>{group.description}</p>
          ) : null}
          <ul className={styles.grid}>
            {group.fields.map((field) => {
              const value = working[field.key] ?? field.defaultValue;
              const isDefault = value.toLowerCase() === field.defaultValue.toLowerCase();
              return (
                <li key={field.key} className={styles.field}>
                  <label className={styles.label} htmlFor={`tf-${field.key}`}>
                    {field.label}
                  </label>
                  <div className={styles.combo}>
                    <input
                      type="color"
                      aria-label={`${field.label} colour swatch`}
                      className={styles.swatch}
                      value={value}
                      onChange={(e) => setField(field.key, e.target.value)}
                    />
                    <input
                      type="text"
                      id={`tf-${field.key}`}
                      className={styles.hex}
                      value={value}
                      maxLength={7}
                      onChange={(e) => setField(field.key, e.target.value)}
                      aria-label={`${field.label} hex value`}
                    />
                    <button
                      type="button"
                      className={styles.resetBtn}
                      onClick={() => resetField(field.key)}
                      disabled={isDefault}
                      title={`Reset to ${field.defaultValue}`}
                      aria-label={`Reset ${field.label} to default`}
                    >
                      Default
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </Card>
      ))}

      <ConfirmModal
        open={resetOpen}
        title="Reset all theme colours?"
        confirmLabel="Reset all"
        variant="danger"
        onConfirm={handleResetAll}
        onCancel={() => setResetOpen(false)}
        message="This will reset every colour back to the system default. Any unsaved changes you have made will be replaced. You will still need to click Save Changes to persist the reset."
      />
    </div>
  );
}
